import React, { useEffect, useMemo } from 'react';
import { FuelTag, FuelTypography } from '@frete.com/fuel-react';
import { useSearchParams } from 'react-router-dom';
import { FreeTagContainer, Header, HeaderContainer, PartnersContainer, Wrapper } from './styled';
import { PartnerSelect } from './PartnerSelect';
import { PARTNER } from '@/types';
import { validate } from '@/utils/validations';
import schema from './validation';

import guep from '@/assets/images/partners/guep.svg';
import skymark from '@/assets/images/partners/skymark.svg';
import { Errors, PartnerList, PartnerOptions } from './types';
import { usePrice, useToast, useTracker } from '@/hooks';
import { useAppContext } from '@/context';
import { useSetFormValue } from '@/utils';

export const Partner: React.FC = () => {
  const { dispatch, state } = useAppContext();
  const tracker = useTracker();
  const form = useMemo(() => ({ ...state.risk.data }), [state.risk.data]);
  const triedToSubmit = useMemo(() => state.risk.triedToSubmit, [state.risk.triedToSubmit]);
  const isResubmit = useMemo(() => Boolean(state.risk.backendResponse?.uuid), [state.risk.backendResponse]);
  const setFormValue = useSetFormValue(dispatch);
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const partnerName = searchParams.get('partner');
  const { registerPrice } = usePrice();

  const partners = useMemo<PartnerList>(
    () => [
      { value: PARTNER.skymark, image: skymark, price: registerPrice.skymark },
      { value: PARTNER.guep, image: guep, price: registerPrice.guep },
    ],
    [registerPrice],
  );

  const previousSelectedPartner = PARTNER[partnerName as PartnerOptions];

  const isRegisterFree = state.risk.farePaid;
  const descriptionText = useMemo(
    () => `Escolha dentre nossas parceiras para enviar este cadastro${isRegisterFree ? ' gratuito.' : '.'}`,
    [isRegisterFree],
  );

  const handleSelectPartner = (partner: PARTNER) => {
    setFormValue('partnerId', partner);
  };

  const handleErrors = () => {
    const errors = validate<Errors>(schema, form);

    const hasErrors = !!errors && !!errors.partnerId;
    dispatch({ action: 'risk_set_is_valid', payload: !hasErrors });
    if (!hasErrors) return;

    if (triedToSubmit) toast.add({ type: 'error', title: errors.partnerId });
  };

  useEffect(handleErrors, [triedToSubmit, form]);
  useEffect(() => {
    tracker.dispatchWithUserData('risk_management_form_gr_viewed');
  }, []);
  useEffect(() => {
    if (previousSelectedPartner) {
      setFormValue('partnerId', previousSelectedPartner);
    }
  }, []);

  return (
    <Wrapper>
      <Header>
        <HeaderContainer>
          <FuelTypography size="heading-sm" weight="bold">
            Gerenciadora de risco
          </FuelTypography>
          {isRegisterFree && (
            <FreeTagContainer>
              <FuelTag color="green" shape="rectangle" highlight label="gratuito" />
            </FreeTagContainer>
          )}
        </HeaderContainer>
        <FuelTypography size="body-md" weight="medium" color="neutral-default">
          {descriptionText}
        </FuelTypography>
      </Header>
      <PartnersContainer>
        {partners.map(({ value, image, price }) => (
          <PartnerSelect
            disabled={isResubmit}
            key={value}
            value={value}
            price={price}
            free={isRegisterFree}
            checked={form.partnerId === value}
            image={image}
            onClick={handleSelectPartner}
          />
        ))}
      </PartnersContainer>
    </Wrapper>
  );
};
